import { Button, cn } from '@massilia/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { IdentificationLoginComponent } from '@/components/login/identification-login-component'
import { ReservationNumberLoginComponent } from '@/components/login/reservation-login-component'
import { toSafeRedirect } from '@/lib/safe-redirect'

const searchSchema = z.object({
  /**
   * Adresse à pré-remplir, portée par les liens « Accéder à mon espace client »
   * des mails. Le client vient de lire un message qui lui est adressé : lui
   * redemander l'adresse à laquelle il l'a reçu est une friction pour rien.
   *
   * Elle ne connecte personne — le mot de passe reste exigé. `.catch(undefined)`
   * comme `redirect` : un paramètre malformé n'a pas à casser la page.
   */
  // oxlint-disable-next-line unicorn/no-useless-undefined -- `.catch()` de zod exige sa valeur de repli
  email: z.string().email().optional().catch(undefined),
  /**
   * Où reprendre après connexion. Accepté tel quel ici puis filtré par
   * `toSafeRedirect` à l'usage — le refuser dès la validation renverrait le
   * client sur une page cassée au lieu de l'accueil. Optionnel volontairement :
   * le rendre obligatoire forcerait les onze `Link to="/connexion"` du site à
   * le fournir.
   */
  // oxlint-disable-next-line unicorn/no-useless-undefined -- `.catch()` de zod exige sa valeur de repli
  redirect: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_auth/connexion')({
  component: LoginComponent,
  validateSearch: searchSchema,
})

const availableConnexionMethods = {
  identification: 'identification',
  pnr: 'pnr',
} as const

export function LoginComponent() {
  const { email, redirect } = Route.useSearch()
  const redirectTo = toSafeRedirect(redirect)
  const [connexionMethod, setConnexionMethod] = useState<keyof typeof availableConnexionMethods>(
    availableConnexionMethods.identification
  )
  const [prefilledEmail, setPrefilledEmail] = useState(email ?? '')

  const handleAccountExists = (email: string) => {
    setPrefilledEmail(email)
    setConnexionMethod(availableConnexionMethods.identification)
    toast.info('Un compte existe déjà pour ce dossier.', {
      description: 'Connectez-vous avec votre mot de passe pour continuer.',
    })
  }

  return (
    <>
      <h1 className="text-center text-xl font-semibold text-gray-light-900">
        Connectez-vous à votre compte <br /> Massilia Voyages
      </h1>
      <div className="flex w-full rounded-lg bg-gray-light-50 p-0.5">
        <button
          className={cn(
            'text-gray-light-600 flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold',
            connexionMethod === availableConnexionMethods.identification &&
              'text-gray-light-900 bg-white'
          )}
          onClick={() => {
            setConnexionMethod(availableConnexionMethods.identification)
          }}
        >
          Avec vos identifiants
        </button>
        <button
          className={cn(
            'text-gray-light-600 flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold',
            connexionMethod === availableConnexionMethods.pnr && 'text-gray-light-900 bg-white'
          )}
          onClick={() => {
            setConnexionMethod(availableConnexionMethods.pnr)
          }}
        >
          Avec le n° de dossier
        </button>
      </div>
      {connexionMethod === availableConnexionMethods.identification && (
        <IdentificationLoginComponent prefilledEmail={prefilledEmail} redirectTo={redirectTo} />
      )}
      {connexionMethod === availableConnexionMethods.pnr && (
        <ReservationNumberLoginComponent
          onAccountExists={handleAccountExists}
          redirectTo={redirectTo}
        />
      )}
      <Link to="/inscription">
        <Button className="w-full text-brand-700 hover:underline" variant="none">
          Je n&apos;ai pas encore de compte
        </Button>
      </Link>
    </>
  )
}
